import { useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom";
import {
  Autocomplete,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { BaseTable } from "../../BaseTable/BaseTable";
import { fDateTime } from "src/utils/formatTime";
import { useFailureStats, useFailedTransactionsList } from "src/hooks/useFailureStats";
import { useSelectedTeam } from "src/hooks/useSelectedTeam";
import axios from "src/util/axios";
import Token from "src/util/Token";
import { getTeamUrl, failedTransactionsUrl } from "src/react-query/endPoints";
import { saveToExcel } from "src/utils/convertAnddownloadCsv";

const DATE_RANGES = [
  { value: "last7days", label: "Last 7 days" },
  { value: "last30days", label: "Last 30 days" },
  { value: "last60days", label: "Last 60 days" },
  { value: "last90days", label: "Last 90 days" },
];

const FAILURE_TYPE_META = {
  zero_kwh: { label: "0 kWh session", color: "warning" },
  orphan_payment_captured: { label: "Paid, no session", color: "error" },
  orphan_payment_failed: { label: "Failed payment, no session", color: "default" },
};

const Kpi = ({ label, value, sub, color }) => (
  <Card variant="outlined" sx={{ height: "100%" }}>
    <CardContent>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h4" sx={{ fontWeight: 700, color: color || "text.primary" }}>
        {value}
      </Typography>
      {sub ? (
        <Typography variant="caption" color="text.secondary">
          {sub}
        </Typography>
      ) : null}
    </CardContent>
  </Card>
);

const listColumns = [
  {
    field: "failureType",
    headerName: "Type",
    minWidth: 180,
    renderCell: ({ value }) => {
      const meta = FAILURE_TYPE_META[value] || { label: value, color: "default" };
      return <Chip size="small" variant="outlined" color={meta.color} label={meta.label} />;
    },
  },
  {
    field: "transactionId",
    headerName: "Transaction",
    minWidth: 120,
    renderCell: ({ value }) =>
      value ? (
        <Link component={RouterLink} to={`/transactions/${value}`}>
          {value}
        </Link>
      ) : (
        "-"
      ),
  },
  {
    field: "paymentId",
    headerName: "Payment",
    minWidth: 160,
    renderCell: ({ value }) =>
      value ? (
        <Link component={RouterLink} to={`/refund-details/${value}`}>
          {value}
        </Link>
      ) : (
        "-"
      ),
  },
  { field: "chargepointId", headerName: "Charger", minWidth: 140, valueFormatter: ({ value }) => value || "-" },
  { field: "connectorId", headerName: "Conn.", minWidth: 80, valueFormatter: ({ value }) => value ?? "-" },
  {
    field: "amount",
    headerName: "Amount (₹)",
    minWidth: 110,
    type: "number",
    valueFormatter: ({ value }) => (value != null ? value : "-"),
  },
  {
    field: "paymentStatus",
    headerName: "Status",
    minWidth: 110,
    valueFormatter: ({ value }) => value || "-",
  },
  {
    field: "eventTime",
    headerName: "Time",
    minWidth: 180,
    flex: 1,
    renderCell: ({ value }) => (value ? fDateTime(value, "yyyy-MM-dd hh:mm:ss a") : "-"),
  },
];

/**
 * "Failed" tab on the Transactions page: failure-rate KPI + per-charger
 * breakdown + a paginated list of the individual failed rows (0-kWh sessions
 * and payments with no session). Rendered only when the tab is shown (gated by
 * the Failed Transactions flag / super-admin in the parent).
 */
export const FailedTransactionsTab = () => {
  const navigate = useNavigate();
  const { teamId } = useSelectedTeam();
  const [searchParams, setSearchParams] = useSearchParams();
  const [exporting, setExporting] = useState(false);
  const [dateRange, setDateRange] = useState(
    () => searchParams.get("frange") || "last30days"
  );

  // Clicking a failed row opens its detail: the transaction for a 0-kWh
  // session, or the payment (refund-details) for a payment with no session.
  const openRowDetail = (params) => {
    const row = params?.row || {};
    if (row.transactionId) {
      navigate(`/transactions/${row.transactionId}`);
    } else if (row.paymentId) {
      navigate(`/refund-details/${row.paymentId}`);
    }
  };
  const { data, isFetching } = useFailureStats({ dateRange });
  const summary = data?.summary;
  const breakdown = useMemo(() => data?.breakdownByCharger ?? [], [data]);

  const [listType, setListType] = useState(() => searchParams.get("ftype") || "");
  const [charger, setCharger] = useState(() => searchParams.get("fcharger") || null); // charger id | null
  const [searchInput, setSearchInput] = useState(() => searchParams.get("fq") || "");
  const [search, setSearch] = useState(() => searchParams.get("fq") || "");
  const [page, setPage] = useState(() => {
    const p = parseInt(searchParams.get("fpage"), 10);
    return Number.isFinite(p) && p > 0 ? p : 0;
  });
  const [pageSize, setPageSize] = useState(25);

  // Debounce the search box (300ms); reset to the first page when it changes.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(0);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Mirror the active filters in the URL so a filtered view is shareable /
  // deep-linkable (e.g. ?tab=failed&fcharger=CB123). State is the source of
  // truth; one-way state -> URL sync (replace, no history spam).
  useEffect(() => {
    const next = new URLSearchParams();
    next.set("tab", "failed");
    if (dateRange && dateRange !== "last30days") next.set("frange", dateRange);
    if (listType) next.set("ftype", listType);
    if (charger) next.set("fcharger", charger);
    if (search) next.set("fq", search);
    if (page) next.set("fpage", String(page));
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, listType, charger, search, page]);

  const { data: listData, isFetching: isListFetching } = useFailedTransactionsList({
    dateRange,
    type: listType || undefined,
    chargepointId: charger || undefined,
    search: search || undefined,
    page,
    pageSize,
  });

  // Hold a single busy indicator until BOTH the stats and the first page of the
  // list have loaded, so the screen renders once instead of flashing through
  // "KPIs only -> empty table -> rows". This is true only on the very first
  // fetch (no data yet AND actively fetching); later filter/page changes keep
  // their previous data (keepPreviousData), so the list updates in place without
  // the full-screen loader.
  const initialLoading =
    (!data && isFetching) || (!listData && isListFetching);

  // Charger options for the dropdown are the chargers the breakdown returned.
  const chargerOptions = useMemo(
    () =>
      (breakdown || []).map((b) => ({
        id: b.chargepointId,
        name: b.chargepointName || b.chargepointId,
      })),
    [breakdown]
  );
  const selectedChargerOption = charger
    ? chargerOptions.find((o) => o.id === charger) || { id: charger, name: charger }
    : null;

  // The top KPI cards track the active charger filter: when a charger is
  // selected they show THAT charger's figures (its per-charger breakdown row
  // already carries failureRate / attempts / failures / zeroKwhSessions /
  // capturedOrphanPayments / orphanPayments), otherwise the whole company. The
  // breakdown is scoped to the date range only, so the type/search filters
  // narrow the list below but do not re-scope the headline cards.
  const effectiveSummary = useMemo(() => {
    if (!charger) return summary;
    return breakdown.find((b) => b.chargepointId === charger) || summary;
  }, [charger, breakdown, summary]);

  const rows = listData?.data ?? [];
  const total = listData?.total ?? 0;

  // Export the CURRENT filtered set (all matching rows, not just the page) to
  // Excel, in one call.
  const handleExport = async () => {
    if (!teamId || total === 0) return;
    setExporting(true);
    try {
      Token.setAuthToken();
      const params = { page: 1, limit: 5000 };
      if (dateRange) params.dateRange = dateRange;
      if (listType) params.type = listType;
      if (charger) params.chargepointId = charger;
      if (search) params.search = search;
      const { data } = await axios.get(getTeamUrl(teamId, failedTransactionsUrl), {
        params,
      });
      const valuesObjArr = (data?.data ?? []).map((r) => ({
        ...r,
        typeLabel: FAILURE_TYPE_META[r.failureType]?.label || r.failureType,
        time: r.eventTime ? fDateTime(r.eventTime, "yyyy-MM-dd hh:mm:ss a") : "",
      }));
      saveToExcel(
        {
          headers: [
            "Type",
            "Transaction ID",
            "Payment ID",
            "Charger",
            "Connector",
            "Amount (INR)",
            "Status",
            "Time",
            "Email",
            "Phone",
          ],
          properties: [
            "typeLabel",
            "transactionId",
            "paymentId",
            "chargepointId",
            "connectorId",
            "amount",
            "paymentStatus",
            "time",
            "email",
            "phone",
          ],
          valuesObjArr,
        },
        `failed-transactions-${dateRange || "range"}`
      );
    } catch (e) {
      // best-effort; leave the UI untouched on failure
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Period</InputLabel>
          <Select
            label="Period"
            value={dateRange}
            onChange={(e) => {
              setDateRange(e.target.value);
              setPage(0);
            }}
          >
            {DATE_RANGES.map((r) => (
              <MenuItem key={r.value} value={r.value}>
                {r.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {initialLoading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 400,
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Kpi
                label="Failure rate"
                value={`${effectiveSummary?.failureRate ?? 0}%`}
                sub={`${effectiveSummary?.failures ?? 0} of ${effectiveSummary?.attempts ?? 0} attempts`}
                color="error.main"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Kpi label="Attempts" value={effectiveSummary?.attempts ?? 0} sub="sessions + payments with no session" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Kpi label="0 kWh sessions" value={effectiveSummary?.zeroKwhSessions ?? 0} sub="started, no energy delivered" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Kpi
                label="Paid, no session"
                value={effectiveSummary?.capturedOrphanPayments ?? 0}
                sub={`${effectiveSummary?.orphanPayments ?? 0} orphan payments total`}
              />
            </Grid>
          </Grid>

          <Card variant="outlined">
            <CardContent>
              <Stack spacing={2} sx={{ mb: 2 }}>
                <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography variant="h6">Failed transactions</Typography>
                  <Chip size="small" label={`${total} result${total === 1 ? "" : "s"}`} />
                  {charger && (
                    <Chip
                      size="small"
                      color="primary"
                      variant="outlined"
                      label={`Charger: ${selectedChargerOption?.name || charger}`}
                      onDelete={() => {
                        setCharger(null);
                        setPage(0);
                      }}
                    />
                  )}
                  <LoadingButton
                    size="small"
                    variant="outlined"
                    onClick={handleExport}
                    loading={exporting}
                    disabled={total === 0}
                    sx={{ ml: "auto" }}
                  >
                    Export
                  </LoadingButton>
                </Stack>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={2}
                  alignItems={{ md: "center" }}
                  flexWrap="wrap"
                  useFlexGap
                >
                  <Autocomplete
                    size="small"
                    sx={{ minWidth: 240 }}
                    options={chargerOptions}
                    getOptionLabel={(o) => o?.name ?? ""}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    value={selectedChargerOption}
                    onChange={(e, val) => {
                      setCharger(val?.id || null);
                      setPage(0);
                    }}
                    renderInput={(params) => <TextField {...params} label="Charger" />}
                  />
                  <TextField
                    size="small"
                    sx={{ minWidth: 280 }}
                    label="Search (txn / payment id, email, charger)"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                  />
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={listType || "all"}
                    onChange={(e, val) => {
                      if (val !== null) {
                        setListType(val === "all" ? "" : val);
                        setPage(0);
                      }
                    }}
                  >
                    <ToggleButton value="all">All</ToggleButton>
                    <ToggleButton value="zero_kwh">0 kWh</ToggleButton>
                    <ToggleButton value="orphan">No session</ToggleButton>
                  </ToggleButtonGroup>
                </Stack>
              </Stack>
              <Box sx={{ minHeight: 400 }}>
                <BaseTable
                  rows={rows}
                  columns={listColumns}
                  getRowId={(row) =>
                    `${row.failureType}-${row.transactionId ?? ""}-${row.paymentId ?? ""}-${row.eventTime}`
                  }
                  isLoading={isListFetching}
                  rowOrCellClickRedirect={openRowDetail}
                  paginationMode="server"
                  rowCount={total}
                  page={page}
                  pageSize={pageSize}
                  onPageChange={(p) => setPage(p)}
                  onPageSizeChange={(s) => {
                    setPageSize(s);
                    setPage(0);
                  }}
                  rowsPerPageOptions={[25, 50, 100]}
                  showExportCsvBtn={false}
                  sx={{
                    // No animation: kill the DataGrid's hover/row CSS
                    // transitions so rows/cells change state instantly.
                    "& .MuiDataGrid-root, & .MuiDataGrid-row, & .MuiDataGrid-cell, & .MuiDataGrid-columnHeader":
                      { transition: "none !important" },
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
};

export default FailedTransactionsTab;
